import * as THREE from 'three';
import { camera, scene } from './scene';
/* ============================================================
   緞帶拖尾：整組 72 條共用**一個** draw call
   ============================================================

   現況的投射物是「一顆會位移的方塊」—— 沒有殘影，所以再快也讀不出速度。
   拖尾是速度感唯一的來源，而且它同時解決另一個問題：子彈飛過去之後，
   玩家有 0.2 秒還看得到「剛剛那一發從哪裡打到哪裡」。

   ## 為什麼是一個 mesh 不是每條一個

   72 條各自一個 mesh = 72 個 draw call，而戰鬥愈激烈愈多 —— 那正是
   `render/fx-instances.ts` 當初要解決的問題。這裡把所有條的頂點放進
   同一個 BufferGeometry，沒用到的條退化成零面積（所有頂點同一點），
   GPU 直接丟掉，成本是零。

   ## 為什麼用頂點色而不是 alpha

   加法混色下「透明」等於「黑」，所以淡出只要把顏色乘向 0 就好，
   不需要 alpha 通道（r128 的 `vertexColors` 預設吃 vec3）。少一個屬性、
   少一次上傳。

   ## 側向量取自鏡頭

   緞帶要面向觀眾才有寬度。飛彈會爬升，用固定的世界上方向會讓垂直段
   退化成一條線。側向量 = 行進方向 × (到鏡頭的方向)，任何朝向都成立。

   ## 洩漏保險

   `trailFree()` 漏掉一次就少一條可用的緞帶，而且不會有任何錯誤。
   所以每條都有 `idle` 計時：0.4 秒沒人餵點就自己收掉。 */

/* ⚠️ **2026-08-05 起這個系統目前沒有任何使用者。**
   兩個消費端都在使用者回饋後拿掉了：飛彈的拖尾改成一般混色的排氣煙團
   （加法緞帶「不論調多暗都是一道光劃過去」），環繞刀刃的拖尾被指為
   「會有個引線，很像魔法」。模組留著是因為它是完整可用的基礎設施
   （72 條共用一個 draw call），但**再接上去之前先想清楚**：
   加法混色的連續線條在這個專案的美術方向裡幾乎一定會讀成魔法。 */
var SLOTS = 72, PTS = 12;

type Trail = {
  used: boolean; dying: number;
  pts: Float32Array;        // PTS × xyz，[0] 是頭
  n: number;                // 已填的點數
  col: THREE.Color; w: number; seg: number; idle: number;
};

var trails: Trail[] = [];
var geo: any = null, mesh: any = null;
var pos: Float32Array | null = null, col: Float32Array | null = null;
var READY = false;

var _d = new THREE.Vector3(), _c = new THREE.Vector3(), _s = new THREE.Vector3();
var _a = new THREE.Vector3(), _b = new THREE.Vector3();

export function initTrails() {
  if (READY) return;
  var verts = SLOTS * PTS * 2;
  pos = new Float32Array(verts * 3);
  col = new Float32Array(verts * 3);
  var idx: number[] = [];
  for (var s = 0; s < SLOTS; s++) {
    var base = s * PTS * 2;
    for (var j = 0; j < PTS - 1; j++) {
      var a = base + j * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3).setUsage(THREE.DynamicDrawUsage));
  geo.setIndex(idx);
  mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
    vertexColors: true, blending: THREE.AdditiveBlending,
    depthWrite: false, transparent: true, side: THREE.DoubleSide
  }));
  mesh.frustumCulled = false; mesh.renderOrder = 5;
  scene.add(mesh);
  for (var i = 0; i < SLOTS; i++) {
    trails.push({
      used: false, dying: 0, pts: new Float32Array(PTS * 3), n: 0,
      col: new THREE.Color(0xffffff), w: 0.3, seg: 1, idle: 0
    });
  }
  READY = true;
}

/** 借一條緞帶。`w` 是頭端半寬，`seg` 是每一節的長度（決定總長 = seg × 12）。
    回 -1 代表池滿 —— 呼叫端要能接受「這一發沒有拖尾」。 */
export function trailGet(color: number, w: number, seg: number) {
  if (!READY) return -1;
  for (var i = 0; i < trails.length; i++) {
    var t = trails[i];
    if (t.used || t.dying > 0) continue;
    t.used = true; t.dying = 0; t.n = 0; t.idle = 0;
    t.col.setHex(color); t.w = w; t.seg = seg;
    return i;
  }
  return -1;
}

/** 餵一個世界座標。每個模擬步呼叫一次；距離不夠就只更新頭，不新增節。 */
export function trailPush(id: number, x: number, y: number, z: number) {
  if (id < 0 || !READY) return;
  var t = trails[id]; if (!t.used) return;
  t.idle = 0;
  var p = t.pts;
  if (t.n === 0) {
    p[0] = x; p[1] = y; p[2] = z; t.n = 1;
    return;
  }
  p[0] = x; p[1] = y; p[2] = z;
  if (t.n < 2) { p[3] = x; p[4] = y; p[5] = z; t.n = 2; return; }
  var dx = x - p[3], dy = y - p[4], dz = z - p[5];
  if (dx * dx + dy * dy + dz * dz < t.seg * t.seg) return;
  // 推一節：整條往後移一格，頭再寫回去
  for (var i = Math.min(t.n, PTS - 1); i > 0; i--) {
    p[i * 3] = p[i * 3 - 3]; p[i * 3 + 1] = p[i * 3 - 2]; p[i * 3 + 2] = p[i * 3 - 1];
  }
  p[0] = x; p[1] = y; p[2] = z;
  if (t.n < PTS) t.n++;
}

/** 還回去。緞帶會在 `fade` 秒內淡掉（預設 0.18）而不是瞬間消失 ——
    子彈打中的那一刻拖尾應該還留在空中。 */
export function trailFree(id: number, fade?: number) {
  if (id < 0 || !READY) return;
  var t = trails[id]; if (!t.used) return;
  t.used = false; t.dying = fade === undefined ? 0.18 : fade;
  (t as any).dieMax = t.dying;
}

function writeSlot(t: Trail, s: number, k: number) {
  var base = s * PTS * 2, p = t.pts;
  if (t.n < 2) { degenerate(base); return; }
  var n = t.n;
  for (var j = 0; j < PTS; j++) {
    var jj = Math.min(j, n - 1);
    _a.set(p[jj * 3], p[jj * 3 + 1], p[jj * 3 + 2]);
    // 行進方向：用前後兩點，端點退一格
    var pv = Math.max(0, jj - 1), nx = Math.min(n - 1, jj + 1);
    _b.set(p[nx * 3], p[nx * 3 + 1], p[nx * 3 + 2]);
    _d.set(p[pv * 3] - _b.x, p[pv * 3 + 1] - _b.y, p[pv * 3 + 2] - _b.z);
    if (_d.lengthSq() < 1e-8) _d.set(0, 0, 1);
    _c.copy(camera.position).sub(_a);
    _s.crossVectors(_d, _c);
    if (_s.lengthSq() < 1e-8) _s.set(1, 0, 0);
    _s.normalize();
    /* 頭寬尾窄；`j >= n` 的多餘節全部貼在尾端（零寬），不會多畫出東西。
       **一度量化成四階**（極簡霓虹幾何那一版），2026-08-05 改回連續：
       階梯狀的殘影是風格化的語言，而飛彈拖的是排氣煙 —— 煙是連續散開的。
       尾端另外往外開一點（`spread`），讓它讀成「愈遠愈散」而不是一條等寬的帶。 */
    var f0 = j >= n ? 0 : 1 - j / (PTS - 1);
    var taper = f0 * (0.30 + 0.70 * f0);
    var spread = 1 + (1 - f0) * 0.9;
    var w = t.w * Math.max(taper, 0.06 * (j < n ? 1 : 0)) * spread;
    var i0 = (base + j * 2) * 3, i1 = i0 + 3;
    pos![i0] = _a.x + _s.x * w; pos![i0 + 1] = _a.y + _s.y * w; pos![i0 + 2] = _a.z + _s.z * w;
    pos![i1] = _a.x - _s.x * w; pos![i1 + 1] = _a.y - _s.y * w; pos![i1 + 2] = _a.z - _s.z * w;
    var f = taper * k;
    var r = t.col.r * f, g = t.col.g * f, b = t.col.b * f;
    col![i0] = r; col![i0 + 1] = g; col![i0 + 2] = b;
    col![i1] = r; col![i1 + 1] = g; col![i1 + 2] = b;
  }
}

function degenerate(base: number) {
  for (var j = 0; j < PTS * 2; j++) {
    var i = (base + j) * 3;
    pos![i] = 0; pos![i + 1] = -9999; pos![i + 2] = 0;
    col![i] = 0; col![i + 1] = 0; col![i + 2] = 0;
  }
}

/** 每畫面幀呼叫一次，在所有 `trailPush` 之後。 */
export function updateTrails(dt: number) {
  if (!READY) return;
  var any = false;
  for (var s = 0; s < trails.length; s++) {
    var t = trails[s];
    if (t.used) {
      t.idle += dt;
      if (t.idle > 0.4) { trailFree(s, 0.12); }   // 洩漏保險：沒人餵就自己收
    }
    if (t.dying > 0) {
      t.dying -= dt;
      if (t.dying <= 0) { t.n = 0; degenerate(s * PTS * 2); any = true; continue; }
    }
    if (!t.used && t.dying <= 0) { if (t.n) { t.n = 0; degenerate(s * PTS * 2); any = true; } continue; }
    var k = t.used ? 1 : Math.max(0, t.dying / ((t as any).dieMax || 0.18));
    writeSlot(t, s, k);
    any = true;
  }
  if (any) {
    geo.attributes.position.needsUpdate = true;
    geo.attributes.color.needsUpdate = true;
  }
}
